export interface LicenceStatus {
  status: 'EXPIRED' | 'EXPIRING_SOON' | 'VALID' | 'NO_DATE';
  isExpired: boolean;
  isExpiringSoon: boolean;
  daysRemaining: number;
  formattedDate: string;
}

export function parseISODateOnly(dateStr?: string | null): Date | null {
  if (!dateStr) return null;
  try {
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

export function checkLicenceStatus(validUpto?: string | null, referenceDate?: Date): LicenceStatus {
  if (!validUpto || !validUpto.trim()) {
    return {
      status: 'NO_DATE',
      isExpired: false,
      isExpiringSoon: false,
      daysRemaining: 0,
      formattedDate: '—',
    };
  }

  const d = parseISODateOnly(validUpto);
  if (!d) {
    return {
      status: 'NO_DATE',
      isExpired: false,
      isExpiringSoon: false,
      daysRemaining: 0,
      formattedDate: validUpto,
    };
  }

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const formattedDate = `${day}-${month}-${year}`;

  const ref = referenceDate ? new Date(referenceDate) : new Date();
  ref.setHours(0, 0, 0, 0);

  const target = new Date(d);
  target.setHours(0, 0, 0, 0);

  const diffMs = target.getTime() - ref.getTime();
  const daysRemaining = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (daysRemaining < 0) {
    return {
      status: 'EXPIRED',
      isExpired: true,
      isExpiringSoon: false,
      daysRemaining,
      formattedDate,
    };
  }

  if (daysRemaining <= 30) {
    return {
      status: 'EXPIRING_SOON',
      isExpired: false,
      isExpiringSoon: true,
      daysRemaining,
      formattedDate,
    };
  }

  return {
    status: 'VALID',
    isExpired: false,
    isExpiringSoon: false,
    daysRemaining,
    formattedDate,
  };
}

export function formatDDMMYYYY(dateInput?: string | Date | null): string {
  if (!dateInput) return '';
  if (dateInput instanceof Date) {
    if (isNaN(dateInput.getTime())) return '';
    const day = String(dateInput.getDate()).padStart(2, '0');
    const month = String(dateInput.getMonth() + 1).padStart(2, '0');
    const year = dateInput.getFullYear();
    return `${day}-${month}-${year}`;
  }
  const clean = String(dateInput).trim();
  if (!clean || clean === '—' || clean === 'N/A' || clean === 'Not known' || clean === 'Inaccessible' || clean === 'First time by us') {
    return clean;
  }
  // YYYY-MM-DD (or YYYY-MM-DDTHH:mm:ss...)
  if (/^\d{4}-\d{2}-\d{2}/.test(clean)) {
    const [y, m, d] = clean.split('T')[0].split('-');
    return `${d.padStart(2, '0')}-${m.padStart(2, '0')}-${y}`;
  }
  // DD/MM/YYYY
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(clean)) {
    const [d, m, y] = clean.split('/');
    return `${d.padStart(2, '0')}-${m.padStart(2, '0')}-${y}`;
  }
  // DD-MM-YYYY
  if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(clean)) {
    const [d, m, y] = clean.split('-');
    return `${d.padStart(2, '0')}-${m.padStart(2, '0')}-${y}`;
  }
  // DD.MM.YYYY
  if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(clean)) {
    const [d, m, y] = clean.split('.');
    return `${d.padStart(2, '0')}-${m.padStart(2, '0')}-${y}`;
  }
  // Fallback parsing
  const d = new Date(clean);
  if (!isNaN(d.getTime())) {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }
  return clean;
}
